"use client";

import React from "react";
import { motion, useReducedMotion } from "framer-motion";
import { ArrowRight, Zap, ShieldCheck, Clock, Sparkles, Workflow, Database, Bot, ChartBar as BarChart3, Calendar, Globe, Search } from 'lucide-react';
import { Button } from "@/components/ui/button";
import { Link } from "@/components/common/Link";
const benefitsData = [{
  index: "01",
  title: "No More Double Entry",
  description: "Automatically sync customer details between booking forms, CRM records, and messaging tools without manual copy-paste.",
  icon: Zap
}, {
  index: "02",
  title: "Fewer Human Errors",
  description: "Standardize lead captures and appointment routing to ensure zero dropped leads or incorrect customer records.",
  icon: ShieldCheck
}, {
  index: "03",
  title: "Faster Response Times",
  description: "Instant AI answers and direct scheduling linkages engage prospects within seconds of their initial query.",
  icon: Clock
}, {
  index: "04",
  title: "A Smoother Customer Experience",
  description: "A clean, unified interaction flow from search citation to final booking leaves clients feeling valued and confident.",
  icon: Sparkles
}];
const spokesData = [{
  id: 1,
  label: "Generative Engine Optimization",
  icon: Search
}, {
  id: 2,
  label: "Local SEO",
  icon: Globe
}, {
  id: 3,
  label: "Website Design",
  icon: Workflow
}, {
  id: 4,
  label: "AI Solutions",
  icon: Bot
}, {
  id: 5,
  label: "CRM & Lead Management",
  icon: Database
}, {
  id: 6,
  label: "Business Automations",
  icon: Zap
}, {
  id: 7,
  label: "Analytics & Reporting",
  icon: BarChart3
}, {
  id: 8,
  label: "Appointment Booking",
  icon: Calendar
}];
export default function Value() {
  const shouldReduceMotion = useReducedMotion();
  const containerVariants = {
    hidden: {
      opacity: 0
    },
    visible: {
      opacity: 1,
      transition: {
        staggerChildren: shouldReduceMotion ? 0 : 0.1
      }
    }
  };
  const itemVariants = {
    hidden: {
      opacity: 0,
      y: shouldReduceMotion ? 0 : 20
    },
    visible: {
      opacity: 1,
      y: 0,
      transition: {
        duration: 0.5,
        ease: "easeOut"
      }
    }
  };
  return <section id="valuefoundationcta" data-nav="light" className="bg-background text-foreground py-24 md:py-36 relative overflow-hidden">
      <div className="max-w-7xl mx-auto px-5 md:px-10">
        {}
        <motion.div initial={{
        opacity: 0,
        y: shouldReduceMotion ? 0 : 15
      }} whileInView={{
        opacity: 1,
        y: 0
      }} viewport={{
        once: true
      }} transition={{
        duration: 0.6
      }} className="mb-16 md:mb-24">
          <span className="font-mono text-xs uppercase tracking-widest text-muted-foreground block mb-4">
            {"WHY IT MATTERS"}
          </span>
          <h2 className="text-3xl md:text-5xl lg:text-6xl font-light tracking-tight max-w-4xl text-foreground leading-tight mb-6">
            {"When Everything Talks, Everything Gets "}
            <span className="font-serif italic text-[var(--accent-deep-red)]">{"Easier"}</span>
            {"."}
          </h2>
          <p className="text-muted-foreground text-base md:text-lg max-w-2xl leading-relaxed">
            Eliminate repetitive manual data entry, reduce human error, and free
            up valuable team time while delivering faster, accurate responses
            to every prospective client.
          </p>
        </motion.div>

        {}
        <motion.div variants={containerVariants} initial="hidden" whileInView="visible" viewport={{
        once: true
      }} className="grid grid-cols-1 sm:grid-cols-2 gap-px bg-border rounded-xl overflow-hidden border border-border shadow-xs my-16 md:my-24">
          {benefitsData.map((benefit, i) => {
          const IconComponent = benefit.icon;
          return <motion.div key={benefit.index} data-index={i} variants={itemVariants} className="bg-card p-8 md:p-12 flex flex-col justify-between group transition-colors duration-300 hover:bg-accent/40">
                <div>
                  <div className="flex items-center justify-between mb-8">
                    <span className="font-serif text-3xl md:text-4xl text-muted-foreground/50 group-hover:text-[var(--accent-deep-red)] transition-colors duration-300">
                      {benefit.index}
                    </span>
                    <div className="p-3 rounded-md bg-muted text-foreground group-hover:bg-[var(--accent-deep-red)]/10 group-hover:text-[var(--accent-deep-red)] transition-colors duration-300">
                      <IconComponent className="w-5 h-5 stroke-[1.25]" />
                    </div>
                  </div>
                  <h3 className="text-xl font-semibold text-card-foreground group-hover:text-foreground mb-3 transition-colors duration-300">
                    {benefit.title}
                  </h3>
                  <p className="text-muted-foreground text-sm leading-relaxed line-clamp-3">
                    {benefit.description}
                  </p>
                </div>
              </motion.div>;
        })}
        </motion.div>

        {}
        <motion.div initial={{
        opacity: 0,
        y: shouldReduceMotion ? 0 : 25
      }} whileInView={{
        opacity: 1,
        y: 0
      }} viewport={{
        once: true
      }} transition={{
        duration: 0.7
      }} className="my-20 md:my-32 py-12 px-6 rounded-2xl bg-card border border-border/60 shadow-xs text-center">
          <div className="max-w-xl mx-auto mb-12">
            <span className="font-mono text-xs uppercase tracking-widest text-muted-foreground block mb-2">
              {"SYSTEM ARCHITECTURE"}
            </span>
            <h3 className="text-2xl md:text-3xl font-light tracking-tight text-card-foreground">
              Connected Operations Hub
            </h3>
          </div>

          <div className="relative max-w-5xl mx-auto py-8">
            {}
            <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-4 gap-4 relative z-10">
              {spokesData.map((spoke, i) => {
              const SpokeIcon = spoke.icon;
              return <div key={spoke.id} data-index={i} className="group bg-background border border-border p-5 rounded-lg flex items-center gap-3 text-left transition-all duration-300 hover:border-[var(--accent-deep-red)]/60 hover:shadow-md">
                    <div className="p-2 rounded bg-muted text-muted-foreground group-hover:text-[var(--accent-deep-red)] group-hover:bg-[var(--accent-deep-red)]/10 transition-colors">
                      <SpokeIcon className="w-4 h-4 stroke-[1.5]" />
                    </div>
                    <span className="text-xs md:text-sm font-medium text-foreground group-hover:text-[var(--accent-deep-red)] transition-colors">
                      {spoke.label}
                    </span>
                  </div>;
            })}
            </div>

            {}
            <div className="mt-10 pt-8 border-t border-border flex justify-center items-center">
              <div className="inline-flex items-center gap-3 px-6 py-3.5 rounded-full bg-[var(--accent-deep-red)] text-white shadow-md font-medium text-sm md:text-base tracking-wide">
                <Workflow className="w-5 h-5" />
                <span>{"System Integrations"}</span>
              </div>
            </div>
          </div>
        </motion.div>

        {}
        <motion.div initial={{
        opacity: 0,
        scale: shouldReduceMotion ? 1 : 0.98
      }} whileInView={{
        opacity: 1,
        scale: 1
      }} viewport={{
        once: true
      }} transition={{
        duration: 0.6
      }} className="bg-card border border-[var(--accent-deep-red)]/40 rounded-2xl p-8 md:p-16 text-center max-w-5xl mx-auto shadow-md relative overflow-hidden">
          <div className="relative z-10 max-w-2xl mx-auto">
            <h3 className="text-2xl md:text-4xl font-light text-card-foreground tracking-tight mb-4">
              See How Your Systems Could Work Together
            </h3>
            <p className="text-muted-foreground text-sm md:text-base mb-8 leading-relaxed">
              Connect lead routing, appointment scheduling, and search
              visibility into one predictable automated pipeline.
            </p>
            <div className="flex flex-col sm:flex-row items-center justify-center gap-4">
              <Link className="bg-[var(--accent-deep-red)] text-white hover:opacity-90 transition-opacity px-8 gap-2 w-full sm:w-auto underline cursor-pointer" to="/PricingPlansPage">
                <Button size="lg" className="inline-flex items-center justify-center gap-2 whitespace-nowrap text-sm font-medium transition-all disabled:pointer-events-none disabled:opacity-50 [&_svg]:pointer-events-none [&_svg:not([class*='size-'])]:size-4 shrink-0 [&_svg]:shrink-0 outline-none focus-visible:border-ring focus-visible:ring-ring/50 focus-visible:ring-[3px] aria-invalid:ring-destructive/20 dark:aria-invalid:ring-destructive/40 aria-invalid:border-destructive text-primary-foreground shadow-xs hover:bg-primary/90 h-10 rounded-md px-6 has-[>svg]:px-4 bg-destructive">Get started now</Button>
              </Link>
              <Link to="/VisibilityCheckupPage" className="w-full sm:w-auto">
                <Button size="lg" variant="outline" className="w-full sm:w-auto border-border text-foreground hover:bg-accent px-8">
                  {"Free AI Visibility Checkup"}
                </Button>
              </Link>
            </div>
          </div>
        </motion.div>
      </div>
    </section>;
}